import {
  computed,
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpWithCacheService } from '../http-with-cache.service';
import { ISecurityGroupTemplate, IServerResponse } from '@aksia/models';
import {
  ISecurityGroup,
  IUserInSecurityGroup,
  ICedricUser,
  IUserRoleService,
  IUser,
  TPreferences,
  TRole,
} from '@aksia/infrastructure';
import { tap } from 'rxjs/operators';
import { rxResource } from '@angular/core/rxjs-interop';
import { CedricApiService } from '../../infrastructure/api/cedric.api.service';

@Injectable({
  providedIn: 'root',
})
export class UserService
  extends HttpWithCacheService
  implements IUserRoleService
{
  private api = inject(CedricApiService);
  private controller: string = 'Users';

  usersResource = rxResource({
    params: () => this.activeUser(),
    stream: () => this.getUsers(),
  });
  users = computed<Array<ICedricUser>>(() => this.usersResource.value() ?? []);

  securityGroupsResource = rxResource({
    params: () => this.activeUser(),
    stream: () => this.getSecurityGroups(),
  });
  securityGroups = computed<Array<ISecurityGroup>>(
    () => this.securityGroupsResource.value() ?? [],
  );

  constructor() {
    super(['users', 'securityGroups']);
  }

  //#region IUserRoleService Implementation

  readonly activeUser = signal<IUser | undefined>(undefined);
  readonly activeRole: Signal<TRole | undefined> = signal(undefined);
  readonly preferences: Signal<TPreferences | undefined> = signal(undefined);
  readonly impersonates: Signal<TRole | undefined> = signal(undefined);
  readonly isAuthenticated: Signal<boolean> = computed(
    () => !!this.activeUser(),
  );
  readonly redirectAfterLogin: WritableSignal<string | undefined> =
    signal(undefined);

  //#endregion

  getUser(): Observable<any> {
    const url = `${this.controller}/GetUser`;
    return this.api.post(url, {}).pipe(catchError(this.handleError));
  }

  getUsers(forceHttp = false): Observable<ICedricUser[]> {
    const url = `${this.controller}/GetUsers`;
    return this.useCache(this.api.post(url, {}), {
      forceHttp,
      cacheKey: 'users',
    });
  }

  getSecurityGroups(forceHttp = false): Observable<ISecurityGroup[]> {
    const url = `${this.controller}/GetSecurityGroups`;
    return this.useCache(this.api.post(url, {}), {
      forceHttp,
      cacheKey: 'securityGroups',
    });
  }

  getUserSecurityGroups(): Observable<ISecurityGroup[]> {
    const url = `${this.controller}/GetUserSecurityGroups`;
    return this.api.post(url, {});
  }

  getSystemInfo(): Observable<string> {
    const url = `${this.controller}/GetSystemInfo`;
    return this.api.post(url, {});
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, body was: `,
        error.error,
      );
    }
    // Return an observable with a user-facing error message.
    return throwError(
      () => new Error('Something bad happened; please try again later.'),
    );
  }

  addUserToSecurityGroup(
    securityGroupUser: Partial<IUserInSecurityGroup>,
  ): Observable<
    IServerResponse & {
      securityGroup: ISecurityGroup;
    }
  > {
    const url = `${this.controller}/CreateSecurityGroupUser`;
    return this.api.post(url, securityGroupUser);
  }

  deleteUserFromSecurityGroup(
    securityGroupUser: Partial<IUserInSecurityGroup>,
  ): Observable<
    IServerResponse & {
      securityGroup: ISecurityGroup;
    }
  > {
    const url = `${this.controller}/DeleteSecurityGroupUser`;
    return this.api.post(url, securityGroupUser);
  }

  deleteUserFromAllSecurityGroups(userId: number): Observable<IServerResponse> {
    const url = `${this.controller}/DeleteUserFromAllSecurityGroups`;
    return this.api.post(url, { userId });
  }

  addSecurityGroup(newGroup: { name: string }): Observable<ISecurityGroup> {
    const url = `${this.controller}/CreateSecurityGroup`;
    return this.api
      .post(url, newGroup)
      .pipe(
        tap((group) =>
          this.updateCache<ISecurityGroup[]>(
            (groups) => [...groups, group],
            'securityGroups',
          ),
        ),
      );
  }

  updateSecurityGroup(
    updatedSecurityGroup: ISecurityGroup,
  ): Observable<ISecurityGroup> {
    const url = `${this.controller}/UpdateSecurityGroup`;
    return this.api.post(url, updatedSecurityGroup).pipe(
      tap(() =>
        this.updateCache<ISecurityGroup[]>((groups) => {
          const idx = groups.findIndex(
            (group) =>
              group.securityGroupId == updatedSecurityGroup.securityGroupId,
          )!;
          groups[idx] = updatedSecurityGroup;
          return groups;
        }, 'securityGroups'),
      ),
    );
  }

  deleteSecurityGroup(
    groupToDelete: ISecurityGroup,
  ): Observable<IServerResponse> {
    const url = `${this.controller}/UpdateSecurityGroup`;
    return this.api
      .post(url, groupToDelete)
      .pipe(
        tap(() =>
          this.updateCache<ISecurityGroup[]>(
            (groups) =>
              groups.filter(
                (group) =>
                  group.securityGroupId !== groupToDelete.securityGroupId,
              ),
            'securityGroups',
          ),
        ),
      );
  }

  getWhereSecurityGroupIsUsed(
    securityGroupId: number,
  ): Observable<{ templates: ISecurityGroupTemplate[] }> {
    return this.api.post(`${this.controller}/GetSecurityGroupTemplates`, {
      securityGroupId,
    });
  }

  //#region IUserRoleService Methods

  readonly initActiveUser = (user: IUser) => {
    this.activeUser.set(user);
  };
  readonly setActiveUser = (user: IUser) => {
    this.activeUser.set(user);
  };
  readonly clearActiveUser = () => {
    this.activeUser.set(undefined);
  };
  readonly isInRole = (role: TRole) => false;
  readonly isAdmin = () => false;
  readonly impersonate = (role: TRole | unknown) => {};
  readonly saveUserPreferences = (preferences: Partial<TPreferences>) => {};

  //#endregion
}
